import { m } from "framer-motion";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
// @mui
import { useTheme, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Drawer, { drawerClasses } from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
// theme
import { paper } from "src/theme/css";
// hooks
import { useBoolean } from "src/hooks/use-boolean";
import { useLocales } from "src/locales";
// components
import Markdown from "src/components/markdown";
import Iconify from "src/components/iconify";
import Scrollbar from "src/components/scrollbar";
import { varHover } from "src/components/animate";
import CustomPopover, { usePopover } from "src/components/custom-popover";
// auth
import { useAuthContext } from "src/auth/hooks";
// api
import { createChat, useGetChatHistory, getChatById } from "src/api/ai";
//
import { newGuid } from "src/core/lib/guid";
import { useChatScroll } from "./hooks";

const MIN_DRAWER_WIDTH = 360;
const MAX_DRAWER_WIDTH = 900;
const DEFAULT_DRAWER_WIDTH = 420;

export default function ChatGPTPopover() {
  const theme = useTheme();
  const { user } = useAuthContext();

  const { t } = useLocales();

  const drawer = useBoolean();
  const popover = usePopover();

  const [prompt, setPrompt] = useState("");

  const [currentConversation, setCurrentConversation] = useState(null);

  // Pregunta ya enviada cuya respuesta todavía no llegó: es lo que sostiene el indicador de carga.
  const [pendingQuestion, setPendingQuestion] = useState(null);

  // El indicador de carga cuenta como un mensaje más para el scroll: si no, en una conversación
  // larga la pregunta en vuelo queda abajo, fuera de la vista.
  const scrollMessages = useMemo(
    () => [...(currentConversation?.chat ?? []), pendingQuestion],
    [currentConversation, pendingQuestion]
  );

  const { messagesEndRef } = useChatScroll(scrollMessages);
  const promptRef = useRef(null);

  const isSubmitting = useBoolean();
  const abortControllerRef = useRef(null);

  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const isResizing = useRef(false);

  const handleResizeMove = useCallback(e => {
    if (!isResizing.current) return;
    // Drawer anchored right: width grows as the cursor moves left.
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= MIN_DRAWER_WIDTH && newWidth <= MAX_DRAWER_WIDTH) {
      setDrawerWidth(newWidth);
    }
  }, []);

  const handleResizeEnd = useCallback(() => {
    isResizing.current = false;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", handleResizeEnd);
    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  const handleResizeStart = e => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const {
    history,
    isLoading: isLoadingHistory,
    isValidating: isValidatingHistory,
    error: historyError,
    mutate: fetchHistory,
  } = useGetChatHistory(drawer.value);

  const isFetchingHistory = isLoadingHistory || isValidatingHistory;

  const handleNewConversation = () => {
    setCurrentConversation(null);
    setPrompt("");
  };

  const handleConversationClick = async id => {
    if (currentConversation?.id === id) return;

    const chat = await getChatById(id);
    setCurrentConversation(chat);
    popover.onClose();
  };

  const sendMessage = async () => {
    if (isSubmitting.value || !prompt.trim()) return;

    const question = prompt;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      isSubmitting.onTrue();

      // La respuesta puede tardar (el modelo encadena tools), así que la pregunta se muestra en la
      // conversación desde ya, con el indicador de carga debajo, y el input queda libre.
      setPendingQuestion(question);
      setPrompt("");

      const chatResponse = await createChat(
        {
          id: currentConversation?.id,
          prompt: question,
        },
        { signal: controller.signal }
      );
      let conversation = null;

      if (currentConversation === null) {
        conversation = {
          id: chatResponse.id,
          title: chatResponse.title,
          chat: [],
        };

        fetchHistory();
      } else {
        conversation = { ...currentConversation };
      }

      const chat = [...conversation.chat];
      chat.push({
        userQuestion: question,
        assistantAnswer: chatResponse.response,
      });

      conversation.chat = chat;

      setCurrentConversation(conversation);
    } catch (error) {
      // If the user cancelled with Stop, the request is aborted here. We intentionally give the
      // prompt text back to the input so it can be edited and resent.
      setPrompt(question);
    }

    setPendingQuestion(null);
    abortControllerRef.current = null;
    isSubmitting.onFalse();
  };

  const handleStop = () => {
    abortControllerRef.current?.abort();
  };

  const handlePromptKeyDown = e => {
    // Enter sends the message; Shift+Enter inserts a new line.
    // Ignore Enter while composing (IME) to not break accented/CJK input.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}
    >
      <Tooltip title={t("AIWarning")}>
        <Iconify
          icon="mdi:information-slab-circle-outline"
          width={20}
        />
      </Tooltip>

      <Typography
        variant="h6"
        sx={{ flexGrow: 1 }}
      >
        Evolution GPT
      </Typography>

      <Tooltip title={t("new")}>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={handleNewConversation}
          sx={{
            width: 40,
            height: 40,
            background: theme => alpha(theme.palette.grey[500], 0.08),
          }}
        >
          <Iconify
            icon="mdi:conversation-plus"
            width={24}
          />
        </IconButton>
      </Tooltip>

      <Tooltip title={t("history")}>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          disabled={isFetchingHistory}
          onClick={popover.onOpen}
          sx={{
            width: 40,
            height: 40,
            background: theme => alpha(theme.palette.grey[500], 0.08),
          }}
        >
          {isFetchingHistory ? (
            <CircularProgress size={24} />
          ) : (
            <Iconify
              icon="mdi:history"
              width={24}
            />
          )}
        </IconButton>
      </Tooltip>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
      >
        <Stack sx={{ p: 1 }}>
          {historyError && history.length === 0 && (
            <Stack
              spacing={1}
              alignItems="flex-start"
              sx={{ px: 1.5, py: 1 }}
            >
              <Typography
                variant="body2"
                sx={{ color: "error.main" }}
              >
                {t("chatHistoryError")}
              </Typography>

              <LoadingButton
                size="small"
                color="inherit"
                variant="outlined"
                loading={isFetchingHistory}
                startIcon={<Iconify icon="mdi:refresh" />}
                onClick={() => fetchHistory()}
              >
                {t("retry")}
              </LoadingButton>
            </Stack>
          )}

          {!historyError && history.length === 0 && (
            <Typography
              variant="body2"
              sx={{ px: 1.5, py: 1, color: "text.secondary" }}
            >
              {t("noChatHistory")}
            </Typography>
          )}

          {history.map(chat => (
            <MenuItem
              key={chat.id}
              onClick={() => handleConversationClick(chat.id)}
            >
              {chat.title}
            </MenuItem>
          ))}
        </Stack>
      </CustomPopover>
    </Stack>
  );

  const renderQuestion = question => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === "light" ? "grey.100" : "grey.800",
          p: 1,
          borderRadius: 1,
          mb: 1,
          textAlign: "right",
          maxWidth: "60%",
        }}
      >
        {question}
      </Box>
    </Box>
  );

  const renderList = (
    <Scrollbar
      sx={{ p: 1 }}
      ref={messagesEndRef}
    >
      {currentConversation &&
        currentConversation.chat.map(message => {
          return (
            <Box
              key={newGuid()}
              sx={{ pb: 4 }}
            >
              {renderQuestion(message.userQuestion)}

              <Markdown children={message.assistantAnswer} />
            </Box>
          );
        })}

      {/* Mientras la IA responde: la pregunta ya en la conversación y el estado de la respuesta. */}
      {pendingQuestion !== null && (
        <Box sx={{ pb: 4 }}>
          {renderQuestion(pendingQuestion)}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: "text.secondary" }}
          >
            <CircularProgress size={16} />

            <Typography variant="body2">{t("generatingAnswer")}</Typography>
          </Stack>
        </Box>
      )}
    </Scrollbar>
  );

  const renderPrompt = (
    <Stack
      ref={promptRef}
      justifyContent="space-between"
      sx={{ p: 2 }}
    >
      <TextField
        multiline
        maxRows={5}
        value={prompt}
        placeholder={t("askAnything")}
        disabled={isSubmitting.value}
        onChange={e => setPrompt(e.target.value)}
        onKeyDown={handlePromptKeyDown}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        {isSubmitting.value ? (
          <Button
            onClick={handleStop}
            sx={{ marginY: "10px", width: 120, flexShrink: 0 }}
            color="error"
            variant="soft"
            startIcon={
              <Iconify
                icon="mdi:stop"
                width={20}
              />
            }
          >
            {t("stop")}
          </Button>
        ) : (
          <LoadingButton
            onClick={sendMessage}
            sx={{ marginY: "10px", width: 120, flexShrink: 0 }}
            color="primary"
            variant="soft"
            endIcon={
              <Iconify
                icon="mdi:send"
                width={20}
              />
            }
          >
            {t("send")}
          </LoadingButton>
        )}
      </Box>
    </Stack>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? "primary" : "default"}
        onClick={drawer.onTrue}
      >
        <Iconify
          icon="ri:openai-fill"
          width={24}
        />
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        sx={{
          [`& .${drawerClasses.paper}`]: {
            ...paper({ theme, bgcolor: theme.palette.background.default }),
          },
        }}
        PaperProps={{
          sx: { width: drawerWidth, maxWidth: "100vw" },
        }}
      >
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: "6px",
            cursor: "col-resize",
            zIndex: theme.zIndex.drawer + 2,
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.24),
            },
          }}
        />

        {renderHead}

        <Divider />

        {renderList}

        {renderPrompt}
      </Drawer>
    </>
  );
}
